import React, { useEffect, useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const AUTOPLAY_INTERVAL = 6500;

export default function Testimonials({ resumeData }) {
  const testimonials = useMemo(
    () =>
      (resumeData?.testimonials || [])
        .map((item, index) => ({
          id: item.id || `${item.name || 'testimonial'}-${index}`,
          quote: item.quote || item.description || '',
          author: item.name || item.author || 'Client',
          role: item.role || item.title || 'Client testimonial',
          company: item.company || '',
          project: item.project || '',
          sample: Boolean(item.sample),
        }))
        .filter((item) => item.quote),
    [resumeData]
  );
  const reduceMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!testimonials.length) {
      setActiveIndex(0);
      return;
    }

    if (activeIndex >= testimonials.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, testimonials.length]);

  useEffect(() => {
    if (testimonials.length < 2 || isPaused) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % testimonials.length);
    }, AUTOPLAY_INTERVAL);

    return () => window.clearInterval(timer);
  }, [isPaused, testimonials.length]);

  if (!testimonials.length) {
    return null;
  }

  const activeTestimonial = testimonials[activeIndex];
  const activeLabel = String(activeIndex + 1).padStart(2, '0');
  const totalLabel = String(testimonials.length).padStart(2, '0');

  const handleSelect = (nextIndex) => {
    setActiveIndex((nextIndex + testimonials.length) % testimonials.length);
  };

  const handleBlurCapture = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsPaused(false);
    }
  };

  return (
    <section id="testimonials" className="section-block" aria-labelledby="testimonials-title">
      <div className="row testimonials-layout">
        <div className="three columns header-col testimonials-header-col">
          <div className="testimonials-intro">
            <p className="section-kicker">Testimonials</p>
            <h2 id="testimonials-title">
              Product teams remember clean execution, fast delivery, and dependable ownership.
            </h2>
            <p className="section-subtitle">
             Feedback that tell stories
            </p>

            <div className="testimonials-proof-strip" aria-label="Delivery strengths">
              <span>SEO-aware builds</span>
              <span>Fast iteration</span>
              <span>Full stack ownership</span>
            </div>

            <div className="testimonials-counter" aria-live="polite">
              <strong>{activeLabel}</strong>
              <span> / {totalLabel}</span>
            </div>
          </div>
        </div>

        <div className="nine columns">
          <div
            className="testimonials-shell"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocusCapture={() => setIsPaused(true)}
            onBlurCapture={handleBlurCapture}
          >
            <div className="testimonials-rail" aria-label="Choose a testimonial">
              {testimonials.map((item, index) => {
                const isActive = index === activeIndex;
                const metaLine = [item.role, item.company].filter(Boolean).join(' • ');

                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`testimonial-chip ${isActive ? 'is-active' : ''}`}
                    onClick={() => handleSelect(index)}
                    aria-pressed={isActive}
                    aria-label={`Show testimonial from ${item.author}`}
                  >
                    <span className="testimonial-chip-index">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="testimonial-chip-copy">
                      <strong>{item.author}</strong>
                      <span>{metaLine || 'Client feedback'}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              className="testimonials-stage"
              role="region"
              aria-roledescription="carousel"
              aria-label="Client testimonials"
            >
              <motion.div
                className="testimonials-track"
                animate={{ x: `${activeIndex * -100}%` }}
                transition={
                  reduceMotion
                    ? { duration: 0 }
                    : { type: 'spring', stiffness: 120, damping: 24, mass: 0.85 }
                }
              >
                {testimonials.map((item, index) => {
                  const isActive = index === activeIndex;
                  const metaLine = [item.role, item.company].filter(Boolean).join(' • ');
                  const badgeLabel = item.sample ? 'Sample quote' : 'Trusted delivery';

                  return (
                    <article
                      key={item.id}
                      className={`testimonial-card ${isActive ? 'is-active' : ''}`}
                      aria-hidden={!isActive}
                    >
                      <div className="testimonial-card-inner">
                        <span className="testimonial-quote-mark" aria-hidden="true">
                          &ldquo;
                        </span>
                        <p className="testimonial-copy">{item.quote}</p>
                        {item.project && (
                          <p className="testimonial-project">
                            <span>Project context</span>
                            {item.project}
                          </p>
                        )}
                        <footer className="testimonial-footer">
                          <div className="testimonial-author">
                            <strong>{item.author}</strong>
                            <span>{metaLine || 'Client testimonial'}</span>
                          </div>
                          <div className="testimonial-badge">{badgeLabel}</div>
                        </footer>
                      </div>
                    </article>
                  );
                })}
              </motion.div>
            </div>

            <div className="testimonials-controls">
              <button
                type="button"
                className="testimonial-nav"
                onClick={() => handleSelect(activeIndex - 1)}
                aria-label="Show previous testimonial"
              >
                Prev
              </button>

              <div className="testimonials-progress" aria-hidden="true">
                <motion.span
                  key={activeTestimonial.id}
                  className="testimonials-progress-bar"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={
                    reduceMotion || isPaused
                      ? { duration: 0 }
                      : { duration: AUTOPLAY_INTERVAL / 1000, ease: 'linear' }
                  }
                />
              </div>

              <button
                type="button"
                className="testimonial-nav"
                onClick={() => handleSelect(activeIndex + 1)}
                aria-label="Show next testimonial"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
